<template>
  <b-modal
    :id="id"
    hide-header
    hide-footer
    centered
    scrollable
    no-close-on-backdrop
    @show="resetParam"
    body-class="d-flex flex-column max-vh-100 position-relative"
  >
    <template #default="{ close }">
      <div
        @dragover.prevent="drag = true"
        @dragleave.prevent="drag = false"
        v-on:drop.prevent="drag = false"
      >
        <drag-area-overlay :drag="drag" v-on:dropFiles="dropFiles" />
        <!-- アップロード中のローディング表示 -->
        <loading-upload v-if="isUploading" :msg="loadingMsg" :fullscreen="false" />

        <b-row class="d-flex" no-gutters>
          <div class="comment-edit-label">コメント編集</div>
        </b-row>
        <b-row no-gutters>
          <b-form-textarea
            class="content-input"
            rows="5"
            v-model="messageSource"
            aria-describedby="content-feedback"
            @update="changeEditMode(editMode)"
          />
          <b-form-text id="content-feedback" :class="contentState ? 'success' : 'warning'">
            {{ validCommentMessage }}
          </b-form-text>
        </b-row>

        <!-- 添付ファイルフォーム -->
        <file-selection-form
          ref="fileSelectionForm"
          contentType="COMMENT"
          label="ファイル"
          :multiple="true"
          :photos="photos"
          :messageId="messageId"
          :commentId="commentId"
          :allow_attachment_type="allow_attachment_type"
          :file-number-limit="10"
          v-on:onUpdateThumb="updateFile"
        />

        <b-row align-h="between" no-gutters>
          <cancel class="col-5" label="キャンセル" v-on:cancel="showEditDiscardAlert(close)" />
          <confirm
            class="col-5"
            label="保存"
            :disabled="!contentState"
            v-on:confirm="onEdit(close)"
          />
        </b-row>
      </div>
    </template>
  </b-modal>
</template>

<style scoped>
/* file-list-viewer の m-2 を打ち消すのと overflow 設定 */
.file-list-viewer-style {
  overflow: auto;
  flex-wrap: nowrap;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.content-edit-label {
  font-size: 14px;
  font-weight: bold;
}

.attachment-link {
  display: flex;
  flex-direction: row;
  justify-items: center;
  margin: 4px;
}

.paperclip {
  color: #000000;
}

.attachement-label {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
  color: #00b7c6;
  cursor: pointer;
}

.content-input {
  background-color: #f3f3f3;
}

.not-allowed {
  color: gray;
  cursor: not-allowed;
}

.number-limit-label {
  color: #00b7c6;
  font-size: 14px;
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useMessageStore, useTopicStore } from "@/store";
import type { UpdateCommentPayload } from "../store";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";
import type { Attachment } from "../model";
import { AttachmentFactory, Comment } from "../model";
import { useTextProcess } from "@/composables/useTextProcess";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import LoadingUpload from "./loading/LoadingUpload.vue";
import DragAreaOverlay from "./DropAreaOverlay.vue";
import FileSelectionForm from "./FileSelectionForm.vue";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";
import S3AccessUtility from "./s3-acceess-utility";

const COMMENT_CONTENT_MAX_LENGTH = 300;

export default defineComponent({
  name: "comment-edit-modal",
  components: {
    Confirm,
    Cancel,
    LoadingUpload,
    DragAreaOverlay,
    FileSelectionForm,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    commentId: { type: String, default: () => "", required: true },
    messageId: { type: String, default: () => "", required: true },
    /** 内容 */
    message: { type: String, default: () => "" },
    /** ファイル */
    photos: { type: Array as PropType<Attachment[]>, default: () => [] },
    // このモーダルが表示されているかのフラグ
    shown: { type: Boolean, default: () => false },
    // Storybook向け
    show: { type: Boolean, default: () => false },
    // ファイル添付設定
    allow_attachment_type: {
      // 元 @Prop は runtime type 引数なし（型注釈のみ）。検証を増やさないため type は付けない。
      default: () => AttachmentFileTypesDefault as AttachmentFileTypes,
    },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const { changeEditMode, showEditDiscardAlert } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const target = ref<File | null>(null);
    const files = ref<(File | Attachment)[]>([]);
    const deletedFiles = ref<Attachment[]>([]); // 削除するファイル

    const messageSource = ref<string>("");

    const replaceParam = ref<{ domainId: string; topicId: string }>({ domainId: "", topicId: "" }); // router.replace用パラメータ

    const prevData = ref<{ message: string; files: (File | Attachment)[] }>({
      message: "",
      files: [],
    });
    /* アップローディング関係 */
    const isUploading = ref<boolean>(false);
    const uploadFiles = ref<number>(0);
    const nowuploads = ref<number>(0);

    const fileTypeErrorMsg = ref<string>("下記の添付ファイルは対応しておりません。");

    const submitLock = ref<boolean>(false);

    const drag = ref<boolean>(false);

    // Vuexの変更監視
    let unsubscribeMutation: (() => void) | undefined;

    // $refs.fileSelectionForm
    const fileSelectionForm = ref<FileSelectionForm | null>(null);

    watch(
      () => props.message,
      (value: string): void => {
        messageSource.value = value;
        prevData.value = { message: props.message, files: props.photos };
      },
      { immediate: true }
    );

    watch(
      () => props.photos,
      (value: Attachment[]): void => {
        files.value = value.slice();
        prevData.value = { message: props.message, files: props.photos };
      },
      { immediate: true }
    );

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    const fileListViewerParams = computed((): { messageId: string; commentId: string } => {
      return { messageId: props.messageId, commentId: props.commentId };
    });

    const paperclipClass = computed((): string => {
      return allow_attachment.value ? "paperclip" : "paperclip not-allowed";
    });

    const attachmentLabelClass = computed((): Record<string, boolean> => {
      return {
        "d-flex attachement-label": true,
        allow: allow_attachment.value,
        "not-allowed": !allow_attachment.value,
      };
    });

    const showCameraButton = computed((): boolean => {
      return useAppStore().isAndroid;
    });

    const contentState = computed((): boolean => {
      if (!messageSource.value) return false;
      const tmp = messageSource.value.trim().replace(/\r?\n/g, "");
      return getLength(tmp) > 0 && getLength(tmp) <= COMMENT_CONTENT_MAX_LENGTH;
    });

    const validCommentMessage = computed((): string => {
      const tmp = messageSource.value.trim().replace(/\r?\n/g, "");
      return getLength(tmp)
        ? `コメントは${COMMENT_CONTENT_MAX_LENGTH}文字以内です(${getLength(tmp)}/${COMMENT_CONTENT_MAX_LENGTH})`
        : "内容は必須項目です";
    });

    const editMode = computed((): boolean => {
      if (messageSource.value != prevData.value.message) return true;
      if (JSON.stringify(files.value) != JSON.stringify(prevData.value.files)) return true;
      return false;
    });

    const loadingMsg = computed((): string => {
      return `添付ファイルを送信中です (${nowuploads.value}/${uploadFiles.value})`;
    });

    const updateFile = (files_: (File | Attachment)[]): void => {
      files.value = files_;

      deletedFiles.value = [];
      // 削除予定ファイルを差分から取得
      prevData.value.files.map((_prevFile) => {
        if (AttachmentFactory.isAttachment(_prevFile)) {
          const exist = files.value.find((file) => {
            return AttachmentFactory.isAttachment(file) && file.url === _prevFile.url;
          });
          if (!exist) {
            deletedFiles.value.push(_prevFile);
          }
        }
      });

      changeEditMode(editMode.value);
    };

    const dropFiles = (files_: FileList): void => {
      const form = fileSelectionForm.value as FileSelectionForm;
      form.dropFiles(files_);
    };

    const resetParam = (): void => {
      submitLock.value = false;
    };

    /* 投稿処理 */
    const onEdit = async (closeEvent: () => void): Promise<void> => {
      // 送信連打による連続送信を防止
      if (submitLock.value) return;
      submitLock.value = true;

      const form = fileSelectionForm.value as FileSelectionForm;

      const s3AccessUtility = S3AccessUtility.getInstance();
      isUploading.value = true;

      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      if (deletedFiles.value) {
        // 削除
        await s3AccessUtility.deleteFiles(deletedFiles.value, {
          domainId: domainId,
          topicId: topicId,
          messageId: props.messageId,
          commentId: props.commentId,
        });
        deletedFiles.value = [];
      }

      let files_: Attachment[] = [];

      const generator = async () => {
        let returnValue: boolean = true;
        for await (const result of s3AccessUtility.uploadFilesGenerator(files.value, {
          domainId: domainId,
          topicId: topicId,
          messageId: props.messageId,
          commentId: props.commentId,
        })) {
          if (!result) {
            returnValue = false;
            break;
          }

          files_ = result.attachments;
          nowuploads.value = result.uploaded;
          uploadFiles.value = result.total;
          if (result.done) {
            returnValue = true;
            break;
          }
        }
        return returnValue;
      };

      // アップロード
      const result = await generator();
      if (!result) {
        rootEvent.emit("show-error-modal", {
          msg: "添付ファイルの送信に失敗しました。",
          afterProcess: () => {
            return;
          },
        });
        return;
      }
      isUploading.value = false;
      nowuploads.value = 0;
      form.resetFileList();

      if (!files_) return;
      const param: Partial<Comment> = {
        message: messageSource.value,
        photos: files_,
      };

      const obj: UpdateCommentPayload = {
        commentId: props.commentId,
        messageId: props.messageId,
        param: param,
      };
      rootEvent.emit("on-comment-edit", obj);
      emit("on-comment-edit", obj);

      changeEditMode(false);
      closeEvent();
    };

    /* モーダルが閉じられた際の処理 */
    const onClose = (closeEvent: () => void): void => {
      emit("closeEditModal");
      closeEvent();
    };

    // 削除後のイベント
    const afterDeleteProcess = (deletedType: string) => {
      switch (deletedType) {
        case "topic":
          console.log("interruption process");
          // modal close => $router.replace
          //emit("interruptionComment", { id: 'modal-comment', path: `/${replaceParam.value.domainId}` });
          vm?.$bvModal.hide("modal-comment");
          vm?.$router.replace(`/${replaceParam.value.domainId}`);
          break;
        case "message":
          console.log("interruption process");
          // modal close
          vm?.$bvModal.hide("modal-comment");
          break;
        default:
          break;
      }
    };

    // Lifecycle
    onBeforeUnmount(() => {
      if (unsubscribeMutation) unsubscribeMutation();
    });

    onMounted(() => {
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }

      // 旧 Vuex の topics/delete・messages/add(削除) mutation 監視を state 変化監視（$subscribe）へ移植。
      // 当該 mutation は action / GraphQL サブスクリプション双方から発火していたため $onAction では捕捉しきれない。
      const topicStore = useTopicStore();
      const messageStore = useMessageStore();
      const openTopicId = (): string => useAppStore().topicId;
      let topicWasPresent = !!topicStore.getOne(useAppStore().domainId, openTopicId());
      const isOpenMessageDeleted = (): boolean => {
        const domainId = useAppStore().domainId;
        const topicId = openTopicId();
        const list = messageStore.get(domainId, topicId) || [];
        const msg = list.find((m) => m.id == props.messageId);
        return !!msg && msg.deleted == true;
      };
      let messageWasDeleted = isOpenMessageDeleted();

      const unsubTopic = topicStore.$subscribe(() => {
        if (!props.shown) return;
        // 「コメント編集中」に「話題」削除 -> 「話題一覧」に移動
        const present = !!topicStore.getOne(useAppStore().domainId, openTopicId());
        if (topicWasPresent && !present) {
          const domainId = useAppStore().domainId;
          const topicId = openTopicId();
          replaceParam.value = { domainId: domainId, topicId: topicId };
          rootEvent.emit("show-error-modal", {
            msg: "話題が削除されたため、話題一覧に移動します",
            afterProcess: () => afterDeleteProcess("topic"),
          });
        }
        topicWasPresent = present;
      });
      const unsubMessage = messageStore.$subscribe(() => {
        if (!props.shown) return;
        //「コメント編集中」に「投稿」削除 -> 「投稿一覧」に移動
        const deleted = isOpenMessageDeleted();
        if (!messageWasDeleted && deleted) {
          rootEvent.emit("show-error-modal", {
            msg: "投稿が削除されたため、投稿一覧に移動します",
            afterProcess: () => afterDeleteProcess("message"),
          });
        }
        messageWasDeleted = deleted;
      });
      unsubscribeMutation = () => {
        unsubTopic();
        unsubMessage();
      };
    });

    return {
      target,
      files,
      deletedFiles,
      messageSource,
      replaceParam,
      prevData,
      isUploading,
      uploadFiles,
      nowuploads,
      fileTypeErrorMsg,
      submitLock,
      drag,
      fileSelectionForm,
      changeEditMode,
      showEditDiscardAlert,
      allow_attachment,
      fileListViewerParams,
      paperclipClass,
      attachmentLabelClass,
      showCameraButton,
      contentState,
      validCommentMessage,
      editMode,
      loadingMsg,
      updateFile,
      dropFiles,
      resetParam,
      onEdit,
      onClose,
      afterDeleteProcess,
    };
  },
});
</script>
